import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Camera, X } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export function PhotosTab({ jobId, project }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/pm/photos", { params: { job_id: jobId, limit: 500 } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  // Group by date for the gallery
  const grouped = useMemo(() => {
    const m = new Map();
    items.forEach((p) => {
      const k = p.taken_at || "(undated)";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  const del = async (id) => {
    if (!confirm("Delete this photo?")) return;
    await api.delete(`/pm/photos/${id}`);
    setViewing(null);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-600">{loading ? "Loading…" : `${items.length} photo${items.length === 1 ? "" : "s"} for `}<b>{project?.title}</b></div>
        <Button onClick={() => setUploadOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-photos-upload">
          <Plus className="w-3.5 h-3.5 mr-2" /> Add photos
        </Button>
      </div>

      {!loading && items.length === 0 && (
        <div className="hard-card p-10 text-center">
          <Camera className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No site photos yet. Bulk upload before/during/after — auditors and claims love them.</p>
        </div>
      )}

      {grouped.map(([date, photos]) => (
        <div key={date} data-testid={`pm-photos-day-${date}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            {date === "(undated)" ? "Undated" : new Date(date + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {photos.length}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {photos.map((p) => (
              <button key={p.id} onClick={() => setViewing(p)} className="relative group aspect-square overflow-hidden border-2 border-zinc-200 hover:border-[#FF5F15] transition-colors" data-testid={`pm-photo-${p.id}`}>
                <AuthImage path={p.storage_path} className="w-full h-full object-cover" />
                {p.caption && <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{p.caption}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}

      {uploadOpen && <PhotoUploadDialog jobId={jobId} onClose={() => setUploadOpen(false)} onSaved={() => { setUploadOpen(false); load(); }} busy={busy} setBusy={setBusy} />}
      {viewing && <PhotoLightbox photo={viewing} onClose={() => setViewing(null)} onDelete={() => del(viewing.id)} onUpdated={load} />}
    </div>
  );
}

function PhotoUploadDialog({ jobId, onClose, onSaved, busy, setBusy }) {
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({ taken_at: todayIso(), location: "", caption: "" });
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const upload = async () => {
    if (!files.length) { toast.error("Pick one or more photos"); return; }
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("job_id", jobId);
        fd.append("file", f);
        Object.entries(meta).forEach(([k, v]) => { if (v !== "" && v !== null) fd.append(k, v); });
        await api.post("/pm/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      } catch (e) {
        // continue on per-file failure
      }
    }
    setBusy(false);
    toast.success(`Uploaded ${ok} of ${files.length}`);
    onSaved();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="pm-photos-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Add site photos</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">Photos *</Label>
            <Input type="file" multiple accept="image/*" capture="environment" onChange={(e) => setFiles(Array.from(e.target.files || []))} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-photos-files" />
            {files.length > 0 && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{files.length} file{files.length === 1 ? "" : "s"} selected</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Date taken</Label>
              <Input type="date" value={meta.taken_at} onChange={(e) => setMeta({ ...meta, taken_at: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-photos-date" />
            </div>
            <div>
              <Label className="label-tag">Location</Label>
              <Input value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Stn 24+50" data-testid="pm-photos-location" />
            </div>
          </div>
          <div>
            <Label className="label-tag">Caption (applies to all)</Label>
            <Input value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="Optional" data-testid="pm-photos-caption" />
          </div>
          {busy && progress.total > 0 && (
            <div className="text-xs">
              Uploading {progress.done} of {progress.total}…
              <div className="h-1.5 bg-zinc-100 mt-1 overflow-hidden">
                <div className="h-full bg-[#FF5F15] transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Cancel</Button>
          <Button onClick={upload} disabled={busy || files.length === 0} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-photos-upload-submit">
            {busy ? "Uploading…" : `Upload ${files.length || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoLightbox({ photo, onClose, onDelete, onUpdated }) {
  const [meta, setMeta] = useState({ caption: photo.caption || "", location: photo.location || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/pm/photos/${photo.id}`, meta);
      toast.success("Saved");
      onUpdated();
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl p-0 overflow-hidden" data-testid="pm-photo-lightbox">
        <div className="bg-black flex items-center justify-center">
          <AuthImage path={photo.storage_path} className="max-h-[60vh] max-w-full object-contain" />
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {photo.taken_at} · uploaded by {photo.uploaded_by_name}
          </div>
          <Input value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} placeholder="Location" className="rounded-sm border-zinc-400 text-sm" />
          <Input value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} placeholder="Caption" className="rounded-sm border-zinc-400 text-sm" />
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" className="text-red-600 rounded-sm text-xs" onClick={onDelete} data-testid="pm-photo-delete"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Close</Button>
            <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-photo-save">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
